/**
 * LawyerSignupPanel — split out of AuthHub.jsx in 2026-02.
 */
import { useState } from "react";

import { toast } from "sonner";
import { IdentificationCard, CheckCircle, EnvelopeSimple, User, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { userSignup, formatUserApiError, acceptTerms } from "../../lib/userApi";
import BarCardScanner from "../../components/signup/BarCardScanner";

import {
  GLASS, FieldLabel, TextInput, PasswordInput, DisplayField,
  GoldButton, GhostBackButton, useAuthShellBackground, parseCardDate,
} from "./_shared";

export default function LawyerSignupPanel({ onPending, onBack }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", confirm: "",
    bar_number: "", phone: "",
    bar_id_number: "", bar_barcode: "",
    bar_validity_grade: "", bar_issue_date: "", bar_validity_until: "",
    date_of_birth: "",
  });
  const [show, setShow] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCardComplete = (data) => {
    // ── Client-side validity gate (2026-07-07) ─────────────────────
    // The backend also enforces this in /auth/signup — this frontend
    // check exists so a lawyer with an expired card learns immediately
    // (before typing password + email) instead of hitting a 422 at the
    // very end and losing their form state.
    //
    // Reads تاريخ النفاذ in the two shapes the AI returns:
    //   • DD/MM/YYYY (canonical, what our prompt asks for)
    //   • YYYY-MM-DD (some cards printed this way; parser tolerates both)
    const vUntil = (data.bar_validity_until || "").trim();
    if (!vUntil) {
      toast.error(
        "تعذَّر قراءة تاريخ نَفاذ البطاقة. أعِد المسح بإضاءة أفضل مع التركيز على منطقة «صالحة لغاية».",
      );
      return;
    }
    const parsed = parseCardDate(vUntil);
    if (!parsed) {
      toast.error("صيغة تاريخ النَّفاذ غير مقروءة. أعِد مسح البطاقة.");
      return;
    }
    const todayBaghdad = new Date(Date.now() + 3 * 3600 * 1000);
    todayBaghdad.setUTCHours(0, 0, 0, 0);
    if (parsed < todayBaghdad) {
      toast.error(
        `بِطاقة نقابة المحامين مُنتَهية الصَّلاحية (تاريخ النَّفاذ: ${vUntil}). التسجيل غير مَسموح — جدِّد البطاقة ثمّ أعِد المُحاولة.`,
        { duration: 8000 },
      );
      return;
    }
    // البطاقة سارية — املأ النموذج
    setForm((f) => ({
      ...f,
      name: data.name || f.name,
      bar_number: data.bar_id_number || f.bar_number,
      bar_id_number: data.bar_id_number || "",
      bar_barcode: data.bar_barcode || "",
      bar_validity_grade: data.bar_validity_grade || "",
      bar_issue_date: data.bar_issue_date || "",
      bar_validity_until: data.bar_validity_until || "",
      date_of_birth: data.date_of_birth || "",
    }));
    setScanned(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!scanned) {
      toast.error("الرجاء مسح بطاقة نقابة المحامين أولاً");
      return;
    }
    if (form.password.length < 8) {
      toast.error("كلمة المرور يجب أن تكون 8 أحرف على الأقل");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setSubmitting(true);
    try {
      const normalisedEmail = form.email.trim().toLowerCase();
      // Consent gate — the user already ticked the unified consent checkbox
      // on the ConsentPanel; replay it here to obtain the short-lived signed
      // token the backend will verify inside /auth/signup.
      const consent = await acceptTerms(normalisedEmail);
      const res = await userSignup({
        ...form,
        email: normalisedEmail,
        terms_token: consent.terms_token,
      });
      toast.success("أرسلنا رمز التحقّق إلى بريدك");
      onPending({ email: res.email, role: "lawyer", name: form.name });
    } catch (err) {
      toast.error(formatUserApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GhostBackButton onClick={onBack} />
      <section className={`${GLASS} p-5 sm:p-6`} data-testid="lawyer-signup-panel">
        <h1 className="text-[22px] font-extrabold text-[#0A192F] leading-tight">إنشاء حساب محامٍ</h1>
        <p className="text-[12.5px] text-[#5A6470] mt-1 mb-5">
          1) امسَح بطاقة النقابة — 2) أكمِل بياناتك — 3) أدخِل رمز التحقّق المُرسَل لبريدك.
        </p>

        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          data-testid="signup-scan-card"
          className={`w-full mb-4 flex items-center justify-center gap-2 py-3.5 rounded-[22px] border-2 text-[14px] font-extrabold transition active:scale-[0.985] ${
            scanned
              ? "bg-[#EFFBF1] border-[#16A34A] text-[#15803D]"
              : "bg-white/75 border-[#D4AF37] text-[#0A192F] hover:bg-white"
          }`}
        >
          {scanned ? <CheckCircle size={20} weight="fill" /> : <IdentificationCard size={20} weight="duotone" />}
          <span>{scanned ? "تم استخراج بياناتك من الهوية — أعد المسح للتحديث" : "مَسح هويّة نقابة المحامين العراقيين"}</span>
        </button>

        <form onSubmit={onSubmit} className="space-y-3.5">
          <div>
            <FieldLabel>الاسم الكامل (من الهوية)</FieldLabel>
            <DisplayField
              testid="signup-name-input"
              value={form.name}
              placeholder="سيُملأ تلقائياً بعد مسح الهوية"
              icon={User}
            />
          </div>
          <div>
            <FieldLabel>رقم النقابة</FieldLabel>
            <DisplayField
              testid="signup-bar-input"
              value={form.bar_number}
              placeholder="من الهوية"
              ltr
              icon={ShieldCheck}
            />
          </div>
          <div>
            <FieldLabel>البريد الإلكتروني *</FieldLabel>
            <TextInput
              data-testid="signup-email-input"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              autoComplete="email"
              ltr
              placeholder="name@example.com"
              icon={EnvelopeSimple}
            />
          </div>
          <div>
            <FieldLabel>كلمة المرور * (8 أحرف على الأقل)</FieldLabel>
            <PasswordInput
              data-testid="signup-password-input"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              show={show}
              onToggle={() => setShow((v) => !v)}
            />
          </div>
          <div>
            <FieldLabel>تأكيد كلمة المرور *</FieldLabel>
            <PasswordInput
              data-testid="signup-password-confirm"
              value={form.confirm}
              onChange={(e) => set("confirm", e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              show={show}
              onToggle={() => setShow((v) => !v)}
            />
          </div>
          <div className="pt-2">
            <GoldButton type="submit" disabled={submitting} testid="signup-submit">
              {submitting ? "جارٍ الإرسال…" : (<><span>إرسال رمز التحقّق</span><ArrowLeft size={18} weight="bold" /></>)}
            </GoldButton>
          </div>
        </form>
      </section>

      <BarCardScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onComplete={handleCardComplete}
      />
    </>
  );
}

